import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Home, Info, Zap, Camera, BookOpen, Shield, Phone } from "lucide-react";
import HeaderLogo from "../../assets/images/header-logo.png";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { path: "/", label: "Home", icon: <Home className="w-4 h-4" /> },
    { path: "/about", label: "About", icon: <Info className="w-4 h-4" /> },
    { path: "/features", label: "Features", icon: <Zap className="w-4 h-4" /> },
    { path: "/gallery", label: "Gallery", icon: <Camera className="w-4 h-4" /> },
    { path: "/blog", label: "Blog", icon: <BookOpen className="w-4 h-4" /> },
  ];

  const isActiveRoute = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleGetStarted = () => {
    navigate("/onboarding");
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer" 
            onClick={() => handleNavigation("/")}
          >
            <img 
              src={HeaderLogo} 
              alt="Aaoghumen" 
              className="h-8 w-auto hover:scale-105 transition-transform duration-200" 
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`flex items-center space-x-1 px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActiveRoute(item.path)
                    ? "text-orange-600 border-b-2 border-orange-600"
                    : "text-gray-700 hover:text-orange-600"
                }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
            
            {/* Additional Links */}
            <button
              onClick={() => handleNavigation("/privacy-policy")}
              className={`flex items-center space-x-1 px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                isActiveRoute("/privacy-policy")
                  ? "text-orange-600 border-b-2 border-orange-600"
                  : "text-gray-700 hover:text-orange-600"
              }`}
            >
              <Shield className="w-4 h-4" />
              <span>Privacy</span>
            </button>
          </div>

          {/* Get Started Button - Desktop */}
          <div className="hidden md:block">
            <button
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Get Started
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-700 hover:text-orange-600 transition-colors p-2"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white shadow-lg">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`flex items-center space-x-3 w-full px-3 py-3 text-left text-sm font-medium transition-colors duration-200 rounded-lg ${
                    isActiveRoute(item.path)
                      ? "text-orange-600 bg-orange-50 border-l-4 border-orange-600"
                      : "text-gray-700 hover:text-orange-600 hover:bg-gray-50"
                  }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ))}
              
              {/* Additional Mobile Links */}
              <button
                onClick={() => handleNavigation("/privacy-policy")}
                className={`flex items-center space-x-3 w-full px-3 py-3 text-left text-sm font-medium transition-colors duration-200 rounded-lg ${
                  isActiveRoute("/privacy-policy")
                    ? "text-orange-600 bg-orange-50 border-l-4 border-orange-600"
                    : "text-gray-700 hover:text-orange-600 hover:bg-gray-50"
                }`}
              >
                <Shield className="w-4 h-4" />
                <span>Privacy Policy</span>
              </button>
              
              {/* Mobile Get Started Button */}
              <div className="pt-4 px-3">
                <button
                  onClick={handleGetStarted}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all duration-200 shadow-lg flex items-center justify-center space-x-2"
                >
                  <span>🚀 Get Started</span>
                </button>
              </div>

              {/* Contact Info in Mobile Menu */}
              <div className="pt-4 px-3 border-t border-gray-200 mt-4">
                <div className="flex items-center space-x-2 text-gray-600 text-sm">
                  <Phone className="w-4 h-4" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="text-gray-500 text-xs mt-1">
                  24/7 Support Available
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;